/**
 * PlanificationStep.tsx
 *
 * Build Bar — Step 1: Planificación.
 *
 * Two-panel layout (no page scroll — each panel scrolls internally):
 *   Left  — editable markdown textarea bound to store.planMarkdown
 *   Right — live rendered markdown preview (react-markdown)
 *
 * On mount, if planMarkdown is empty, automatically triggers AI plan generation
 * via useGeneratePlan. During generation both panels show skeleton loaders.
 *
 * An AiAssistInput at the bottom lets the user ask the AI to modify the plan.
 * The "Aprobar plan" button at the bottom-right advances to Build step 2.
 *
 * Context miniatures at the top show the selected plantilla, themes, and
 * providers so the user can quickly verify the configuration without leaving
 * the step.
 */

import { useEffect, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { Button } from '@/components/ui';
import toast from 'react-hot-toast';

import { extractApiError } from '@/lib/axios';
import { useWizardStore } from '@/stores/useWizardStore';
import { useGeneratePlan, usePlanAssist, useUpdateDraft } from '@/api/useWizardV2';
import { usePlantillas, useThemes } from '@/api/usePresentationWizard';
import type { NarrativePlan } from '@/types/wizardV2';
import { AiAssistInput } from '../components/AiAssistInput';
import styles from './PlanificationStep.module.css';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PlanificationStep — Build bar step 1.
 *
 * Reads and writes plan state exclusively from/to useWizardStore.
 * TanStack Query mutations (useGeneratePlan, usePlanAssist) are the only
 * server-communication paths; results are committed to the store on success.
 */
export const PlanificationStep = () => {
  // ── Store ──────────────────────────────────────────────────────────────────

  const {
    presentationId,
    formData,
    selectedProviders,
    plan,
    planMarkdown,
    isPlanGenerating,
    planAssistError,
    setPlan,
    setPlanMarkdown,
    setIsPlanGenerating,
    setPlanAssistError,
    goToBuildStep,
    returnToSetup,
    goToSetupStep,
  } = useWizardStore();

  // ── Catalog data (for context miniatures) ──────────────────────────────────

  const { data: plantillas } = usePlantillas();
  const { data: themes } = useThemes();

  // ── API mutations ──────────────────────────────────────────────────────────

  const generatePlan = useGeneratePlan();
  const planAssist = usePlanAssist();
  const updateDraft = useUpdateDraft();

  // ── Fallback: generate plan if the parent shell didn't start it ────────────

  useEffect(() => {
    if (!planMarkdown && !isPlanGenerating && !generatePlan.isPending && presentationId) {
      setIsPlanGenerating(true);
      generatePlan.mutate(
        { id: presentationId },
        {
          onSuccess: (result) => {
            setPlan(result);
            setPlanMarkdown(buildMarkdownFromPlan(result));
            setIsPlanGenerating(false);
          },
          onError: (err) => {
            setIsPlanGenerating(false);
            toast.error(extractApiError(err, 'Could not generate the plan.'), { duration: 8_000 });
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentationId]);

  // ── AI Assist handler ──────────────────────────────────────────────────────

  /**
   * Called by AiAssistInput when the user submits a modification instruction.
   * Sends the message to the plan-chat endpoint and updates the store on success.
   * Throws on failure so AiAssistInput can keep the input text for retry.
   */
  const handleAssistSubmit = useCallback(
    async (instruction: string) => {
      if (!presentationId) return;

      // Clear any previous error before submitting.
      setPlanAssistError(null);

      try {
        const result = await planAssist.mutateAsync({
          id: presentationId,
          instruction,
          currentPlanMd: planMarkdown,
        });

        if (result.plan) {
          setPlan(result.plan);
        }
        const md = result.markdown || buildMarkdownFromPlan(result.plan);
        setPlanMarkdown(md);

        // Auto-save the updated plan markdown to the draft.
        updateDraft.mutate({
          id: presentationId,
          data: { plan_markdown: md },
        });
      } catch (err) {
        const message = extractApiError(err, 'Error modifying the plan. Please try again.');
        setPlanAssistError(message);
        throw err; // Re-throw so AiAssistInput retains the input text.
      }
    },
    [presentationId, planMarkdown, planAssist, setPlan, setPlanMarkdown, setPlanAssistError, updateDraft],
  );

  // ── Textarea change handler ────────────────────────────────────────────────

  /**
   * Sync textarea changes directly to the store.
   * Debounced auto-save is intentionally skipped here to keep the component
   * lean; the parent wizard shell already handles draft auto-save.
   */
  const handleMarkdownChange = useCallback(
    (value: string) => {
      setPlanMarkdown(value);
    },
    [setPlanMarkdown],
  );

  // ── Context miniature helpers ──────────────────────────────────────────────

  /** Find the selected plantilla object from the catalog. */
  const selectedPlantilla = plantillas?.find((p) => p.slug === formData.plantilla_slug);

  /**
   * Find all selected theme objects from the catalog.
   * `formData.theme_slugs` is an ordered array; show at most the first two
   * in the strip to avoid overflow on narrow viewports.
   */
  const selectedThemes = themes?.filter((t) => formData.theme_slugs.includes(t.slug)) ?? [];

  /** Providers shown individually (max 3 before "+ N más" chip). */
  const MAX_VISIBLE_PROVIDERS = 3;
  const visibleProviders = selectedProviders.slice(0, MAX_VISIBLE_PROVIDERS);
  const hiddenProviderCount = Math.max(0, selectedProviders.length - MAX_VISIBLE_PROVIDERS);

  // ── Derived loading state ──────────────────────────────────────────────────

  const isGenerating = isPlanGenerating || generatePlan.isPending;
  const isAssisting = planAssist.isPending;
  const showSkeletons = isGenerating || isAssisting;

  // ── Animated step checklist while loading ──────────────────────────────────

  const [planStep, setPlanStep] = useState(0);

  useEffect(() => {
    if (!showSkeletons) {
      setPlanStep(0);
      return;
    }
    const steps = isAssisting ? ASSIST_STEPS : PLAN_STEPS;
    const id = setInterval(
      () => setPlanStep((s) => Math.min(s + 1, steps.length - 1)),
      PLAN_STEP_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, [showSkeletons, isAssisting]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      {/* ── Context miniatures strip ─────────────────────────────────────── */}
      <div className={styles.contextStrip} role="navigation" aria-label="Selected configuration">
        <span className={styles.contextLabel}>Configuration</span>

        {/* Plantilla miniature */}
        {selectedPlantilla && (
          <>
            <div className={styles.stripDivider} aria-hidden />
            <button
              type="button"
              className={styles.miniCard}
              onClick={() => {
                returnToSetup();
                goToSetupStep(2);
              }}
              title={`Template: ${selectedPlantilla.name} — click to change`}
              aria-label={`Template ${selectedPlantilla.name}, click to go back and change`}
            >
              {selectedPlantilla.preview_image ? (
                <img
                  src={selectedPlantilla.preview_image}
                  alt=""
                  className={styles.miniCardThumb}
                  aria-hidden
                />
              ) : (
                <div className={styles.miniCardThumbPlaceholder} aria-hidden />
              )}
              <span className={styles.miniCardName}>{selectedPlantilla.name}</span>
              <span className={`${styles.miniCardBadge} ${styles.miniCardBadgePlantilla}`}>
                Template
              </span>
            </button>
          </>
        )}

        {/* Theme miniatures (up to 2) */}
        {selectedThemes.slice(0, 2).map((theme) => (
          <button
            key={theme.slug}
            type="button"
            className={styles.miniCard}
            onClick={() => {
              returnToSetup();
              goToSetupStep(3);
            }}
            title={`Theme: ${theme.name} — click to change`}
            aria-label={`Theme ${theme.name}, click to go back and change`}
          >
            <div
              className={styles.miniCardThumbPlaceholder}
              style={{
                background: theme.color_suggestion?.primary ?? 'var(--color-bg-muted)',
              }}
              aria-hidden
            />
            <span className={styles.miniCardName}>{theme.name}</span>
            <span className={`${styles.miniCardBadge} ${styles.miniCardBadgeTheme}`}>Theme</span>
          </button>
        ))}

        {/* Provider chips */}
        {visibleProviders.length > 0 && (
          <>
            <div className={styles.stripDivider} aria-hidden />
            {visibleProviders.map((provider) => (
              <div
                key={provider.id}
                className={styles.providerChip}
                title={provider.nombre_comercial}
              >
                {provider.image ? (
                  <img
                    src={provider.image}
                    alt=""
                    className={styles.providerAvatar}
                    aria-hidden
                  />
                ) : (
                  <div
                    className={styles.providerAvatarFallback}
                    aria-hidden
                  >
                    {provider.nombre_comercial.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className={styles.providerName}>{provider.nombre_comercial}</span>
              </div>
            ))}

            {hiddenProviderCount > 0 && (
              <span className={styles.moreChip} aria-label={`${hiddenProviderCount} more providers`}>
                +{hiddenProviderCount} more
              </span>
            )}
          </>
        )}
      </div>

      {/* ── Two-panel editor area ────────────────────────────────────────── */}
      <div className={styles.editorArea}>
        {showSkeletons ? (
          /* Loading state — animated step-by-step checklist */
          <div className={styles.planLoadingCard} aria-label="Generating plan..." aria-busy role="status">
            <div className={styles.planLoadingShimmer} aria-hidden />
            <p className={styles.planLoadingTitle}>
              {isAssisting ? 'Applying your changes...' : 'Building your event plan...'}
            </p>
            <ol className={styles.planChecklist} aria-label="Generation steps">
              {(isAssisting ? ASSIST_STEPS : PLAN_STEPS).map((label, i) => {
                const state = i < planStep ? 'done' : i === planStep ? 'active' : 'pending';
                return (
                  <li key={label} className={`${styles.planChecklistItem} ${styles[`item_${state}`]}`}>
                    <span className={styles.planChecklistIcon} aria-hidden>
                      {state === 'done' ? '✓' : state === 'active' ? '●' : '○'}
                    </span>
                    <span className={styles.planChecklistLabel}>{label}</span>
                  </li>
                );
              })}
            </ol>
            <div className={styles.planProgressBar} role="progressbar" aria-valuenow={planStep} aria-valuemax={(isAssisting ? ASSIST_STEPS : PLAN_STEPS).length - 1}>
              <div
                className={styles.planProgressFill}
                style={{ width: `${((planStep + 1) / (isAssisting ? ASSIST_STEPS : PLAN_STEPS).length) * 100}%` }}
              />
            </div>
          </div>
        ) : (
          /* Loaded state — left textarea + right preview */
          <>
            {/* Left: editable markdown */}
            <div className={styles.editorPanel}>
              <div className={styles.panelHeader}>
                <div className={`${styles.panelHeaderDot} ${styles.dotEditor}`} aria-hidden />
                <h2 className={styles.panelHeaderTitle}>Editor Markdown</h2>
              </div>
              <textarea
                className={styles.mdTextarea}
                value={planMarkdown}
                onChange={(e) => handleMarkdownChange(e.target.value)}
                spellCheck={false}
                aria-label="Presentation plan in Markdown, editable"
                aria-multiline
              />
            </div>

            {/* Right: rendered preview */}
            <div className={styles.previewPanel}>
              <div className={styles.panelHeader}>
                <div className={`${styles.panelHeaderDot} ${styles.dotPreview}`} aria-hidden />
                <h2 className={styles.panelHeaderTitle}>Preview</h2>
              </div>
              <div
                className={styles.previewContent}
                aria-label="Plan preview"
                aria-live="polite"
              >
                {planMarkdown ? (
                  <ReactMarkdown>{planMarkdown}</ReactMarkdown>
                ) : (
                  <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                    The plan will appear here once generated.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── AI Assist input ──────────────────────────────────────────────── */}
      <div className={styles.assistFooter}>
        <AiAssistInput
          placeholder="Ask AI to modify the plan..."
          onSubmit={handleAssistSubmit}
          isLoading={isAssisting}
          error={planAssistError}
          onClearError={() => setPlanAssistError(null)}
        />
      </div>

      {/* ── Approve plan button ──────────────────────────────────────────── */}
      <div className={styles.approveRow}>
        <Button
          variant="primary"
          size="md"
          onClick={() => goToBuildStep(3)}
          disabled={!plan && !planMarkdown}
          aria-label="Approve the plan and continue to the slides step"
        >
          Approve plan &#8594;
        </Button>
      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PLAN_STEPS = [
  'Reading your event brief',
  'Analyzing selected providers',
  'Structuring presentation sections',
  'Mapping the event journey',
  'Writing narrative content',
  'Finalizing your plan',
];

const ASSIST_STEPS = [
  'Understanding your request',
  'Reviewing current plan',
  'Preparing modifications',
  'Applying AI changes',
];

const PLAN_STEP_INTERVAL_MS = 3_500;

/**
 * Convert a NarrativePlan object into a human-readable Markdown string.
 * Used as the initial value for the editable textarea.
 *
 * If the backend already sends a `markdown` field directly on the plan
 * object (as a non-standard extension), that value is returned as-is.
 * Otherwise the function synthesises markdown from the structured data.
 *
 * @param plan - Structured narrative plan from the backend.
 * @returns Markdown string suitable for display in the editor textarea.
 */
export function buildMarkdownFromPlan(plan: NarrativePlan | null): string {
  if (!plan) return '';

  // Type cast to access a potential `markdown` field that the backend may
  // include as a non-standard extension alongside the structured data.
  const extended = plan as NarrativePlan & { markdown?: string };
  if (extended.markdown) return extended.markdown;

  const lines: string[] = [];

  // Governing thought as the H1.
  lines.push(`# ${plan.governing_thought}`);
  lines.push('');

  // Each section as an H2 with supporting details.
  plan.sections.forEach((section, i) => {
    lines.push(`## ${i + 1}. ${section.name}`);
    lines.push('');
    lines.push(`**Narrative role:** ${section.narrative_role}`);
    lines.push('');
    lines.push(`**Key assertion:** ${section.assertion}`);
    lines.push('');
    lines.push(`**Suggested slides:** ${section.slide_count}`);
    lines.push('');
    if (section.key_evidence.length > 0) {
      lines.push('**Key evidence:**');
      lines.push('');
      section.key_evidence.forEach((ev) => {
        lines.push(`- ${ev}`);
      });
      lines.push('');
    }
  });

  return lines.join('\n');
}
